import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';

import { saveEngagementDraft } from './documentEngagement.slice';
import { copyObject } from '../utils/common';
import { SummaryNoteTypeEnum } from '../Enum/SummaryNoteTypeEnum';
import { gotoNextMenu, setAssessmentError } from './assessment.slice';
import { CurrentNeedsModel } from '../Models/CurrentNeedsModel/CurrentNeedsModel.model';
import { CurrentNeedOption } from '../Models/CurrentNeedsModel/CurrentNeedOption.model';
import { SingleNeed } from '../Models/CurrentNeedsModel/SingleNeed.model';
import { preparePriorAssessmentForNewDraft, sanitizeAiFlagsForEmptyAnswers } from '../utils/aiFlagHelpers';

const currentNeedsSlice = createSlice({
    name: 'currentNeedsSlice',
    initialState: {
        currentNeedsInfo: new CurrentNeedsModel(),
        currentNeedsOptions: [] as CurrentNeedOption[],
        isFetchingCurrentNeeds: false,
        modalType: '',
        selectedNeed: new SingleNeed(),
    },
    reducers: {
        _setCurrentNeedsInfo(state, action) {
            state.currentNeedsInfo = action.payload;
        },
        _setCurrentNeedOptions(state, action) {
            state.currentNeedsOptions = action.payload;
        },
        _setIsFetchingCurrentNeeds(state, action) {
            state.isFetchingCurrentNeeds = action.payload;
        },
        _setModalType(state, action) {
            state.modalType = action.payload;
        },
        _setSelectedNeed(state, action) {
            state.selectedNeed = action.payload;
        },
    },
});

const { _setCurrentNeedsInfo, _setCurrentNeedOptions, _setIsFetchingCurrentNeeds, _setModalType, _setSelectedNeed } = currentNeedsSlice.actions;

export const setCurrentNeedsInfo =
    (currentNeedsInfo: CurrentNeedsModel): AppThunk =>
    async (dispatch) => {
        dispatch(_setCurrentNeedsInfo(currentNeedsInfo));
    };

export const setCurrentNeedOptions =
    (currentNeedsOptions: CurrentNeedOption[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setCurrentNeedOptions(currentNeedsOptions));
    };

export const setIsFetchingCurrentNeeds =
    (isFetching: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingCurrentNeeds(isFetching));
    };

export const setModalType =
    (modalType: string): AppThunk =>
    async (dispatch) => {
        dispatch(_setModalType(modalType));
    };

export const setSelectedNeed =
    (selectedNeed: SingleNeed): AppThunk =>
    async (dispatch) => {
        dispatch(_setSelectedNeed(selectedNeed));
    };

export const saveNeeds =
    (currentNeedsInfo: CurrentNeedsModel): AppThunk =>
    async (dispatch) => {
        const isUpdate = Boolean(currentNeedsInfo.Id);
        const url = isUpdate ? `${API_URL}/currentneeds/updatecurrentneeds/` : `${API_URL}/currentneeds/insertcurrentneeds/`;
        const response = await axios.put(url, currentNeedsInfo);
        dispatch(setCurrentNeedsInfo(response.data));
        dispatch(setModalType(''));
    };

export const handleConfirmDeleteNeed =
    (selectedNeed: SingleNeed): AppThunk =>
    async (dispatch, getState) => {
        if (selectedNeed) {
            const currentNeedsInfo = getState().currentNeedsSlice.currentNeedsInfo;
            const updatedNeedsList = currentNeedsInfo.CurrentNeedsList.filter((need) => need.Id !== selectedNeed.Id);
            const updatedCurrentNeedsInfo = { ...currentNeedsInfo };
            updatedCurrentNeedsInfo.CurrentNeedsList = updatedNeedsList;
            dispatch(saveNeeds(updatedCurrentNeedsInfo));
        }
    };

export const handleConfirmAddNeed =
    (singleNeed: SingleNeed, memberId: string): AppThunk =>
    async (dispatch, getState) => {
        if (singleNeed) {
            const currentNeedsInfo = getState().currentNeedsSlice.currentNeedsInfo;

            const copy = copyObject(currentNeedsInfo);
            copy.MemberId = memberId;
            copy.CurrentNeedsList = copy.CurrentNeedsList || [];
            copy.CurrentNeedsList = [...copy.CurrentNeedsList, singleNeed];
            dispatch(saveNeeds(copy));
        }
    };

export const handleConfirmEditNeed =
    (singleNeed: SingleNeed): AppThunk =>
    async (dispatch, getState) => {
        if (singleNeed) {
            const currentNeedsInfo = getState().currentNeedsSlice.currentNeedsInfo;
            const copy = copyObject(currentNeedsInfo);
            const updatedNeedsList = copy.CurrentNeedsList.map((need) => (need.Id === singleNeed.Id ? singleNeed : need));
            copy.CurrentNeedsList = updatedNeedsList;
            dispatch(saveNeeds(copy));
        }
    };

export const saveCurrentNeedsAssessment =
    (currentNeedsModel: CurrentNeedsModel, _saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch) => {
        const isUpdate = Boolean(currentNeedsModel.Id);
        const url = isUpdate ? `${API_URL}/currentneeds/updatecurrentneeds/` : `${API_URL}/currentneeds/insertcurrentneeds/`;

        try {
            const response = await axios.put(url, currentNeedsModel);
            const savedCurrentNeeds = isUpdate ? currentNeedsModel : response.data;
            if (Boolean(response.data)) {
                dispatch(setCurrentNeedsInfo(isUpdate ? currentNeedsModel : { ...currentNeedsModel, Id: response.data.Id }));
            }
            const isDraftSaved = await dispatch(prepareCurrentNeedsNotes(savedCurrentNeeds));
            if (gotoNext && isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        }
    };

const prepareCurrentNeedsNotes =
    (currentNeedsInfo: CurrentNeedsModel): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(currentNeedsInfo.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.CurrentNeeds);
            summaryNotes.push({
                Id: currentNeedsInfo.MemberId,
                CptCode: '',
                Zcode: '',
                Note: 'Current Needs Assessment Completed',
                Type: SummaryNoteTypeEnum.CurrentNeeds,
                IsConfirm: true,
                DisplayName: 'Current Needs',
            });
            engagementDraft.SummaryNotes = summaryNotes;
            const updatedMemberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                CurrentNeedsStatus: currentNeedsInfo.AssessmentStatus,
            };
            const updatedEngagementDraft = {
                ...engagementDraft,
                CurrentNeedsId: currentNeedsInfo.Id,
            };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
                EngagementDraft: updatedEngagementDraft,
            };
            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const fetchCurrentNeedsInfo =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingCurrentNeeds(true));
        const response = await axios.get(`${API_URL}/currentneeds/fetchcurrentneedsbyid?id=${id}`);
        if (response.data?.Id) {
            dispatch(setCurrentNeedsInfo(response.data));
        } else {
            dispatch(setCurrentNeedsInfo(new CurrentNeedsModel()));
        }
        dispatch(setIsFetchingCurrentNeeds(false));
    };

export const fetchCurrentNeedOptions = (): AppThunk => async (dispatch, getState) => {
    dispatch(setIsFetchingCurrentNeeds(true));
    const response = await axios.get(`${API_URL}/currentneeds/fetchcurrentneedoptions`);
    const labelsOnly = response.data.map((item: CurrentNeedOption) => {
        return {
            OptionLabel: item.OptionLabel,
        };
    });
    dispatch(setCurrentNeedOptions(labelsOnly));
    dispatch(setIsFetchingCurrentNeeds(false));
};

export const fetchCurrentNeedsByMemberId =
    (memberId: string, forNewDraft = false): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/currentneeds/fetchcurrentneedsbymemberid?memberId=${memberId}`);
        if (response.data?.Id) {
            if (forNewDraft) {
                preparePriorAssessmentForNewDraft(response.data);
            } else {
                sanitizeAiFlagsForEmptyAnswers(response.data);
            }
            dispatch(setCurrentNeedsInfo(response.data));
        } else {
            dispatch(setCurrentNeedsInfo(new CurrentNeedsModel()));
        }
    };

export default currentNeedsSlice.reducer;
